import type { TreeModel, BBSymbolLink, StoryElementReference, StorySymbols, StoryVersionType } from 'types/story';
import { componentWalk } from './component-walk';
import { getSymbolModel } from './get-symbol-model';
import { isStoryElementReference, resolveStoryElement, createStoryElementReference } from './story-element-reference';

type Props = {
	// card elements
	elements: (TreeModel | BBSymbolLink | StoryElementReference)[];
	symbols: StorySymbols;
	// provide story elements in order to take component data from there for story element references
	storyElements?: StoryVersionType['data']['elements'];
	initialPath?: string;
	callback: (p: {
		component: TreeModel;
		index: number;
		path: string;
		parent?: TreeModel;
		// Stop main componentWalk
		stopAll: () => void;
		// Stop current walk callback recursion
		stopCurrent: () => void;
	}) => void;
	/** Invalid symbol placements are reported separately from resolved model visits. */
	onSymbolError?: (p: { component: BBSymbolLink; path: string; error: unknown }) => void;
	// Optional callback used for debugging purposes to be called on each iteration within componentWalk.
	debugIterationCallback?: (props: Parameters<Parameters<typeof componentWalk>[1]>[0]) => void;
};

/** Walk models, resolving symbol links and story references without changing card paths. */
export function componentWalkFull({
	elements,
	symbols,
	storyElements,
	callback,
	initialPath = '',
	debugIterationCallback,
	onSymbolError,
}: Props) {
	let stopped = false;
	const stopAll = () => {
		stopped = true;
	};
	function walk(
		nodes: (TreeModel | BBSymbolLink | StoryElementReference)[] | TreeModel['children'],
		path: string,
		parent?: TreeModel
	) {
		if (!Array.isArray(nodes)) return;
		for (let index = 0; index < nodes.length && !stopped; index += 1) {
			const entry = nodes[index];
			const childPath = path === '' ? `${index}` : `${path}.children.${index}`;
			let value: TreeModel | BBSymbolLink | undefined = isStoryElementReference(entry)
				? resolveStoryElement(entry, storyElements)
				: entry;
			if (!value) continue;
			if (
				'_id' in value &&
				storyElements &&
				parent &&
				(parent.type === 'FLOAT_ABOVE' || parent.type === 'FLOAT_BELOW')
			) {
				value = resolveStoryElement(createStoryElementReference(value._id), storyElements) ?? value;
			}
			let component: TreeModel;
			if ('_id' in value) {
				component = value;
			} else {
				try {
					component = getSymbolModel(value, symbols);
				} catch (error) {
					onSymbolError?.({ component: value, path: childPath, error });
					console.error((error as Error).message);
					continue;
				}
			}
			let skipChildren = false;
			const payload = { component, index, path: childPath, parent };
			debugIterationCallback?.(payload);
			callback({
				...payload,
				stopCurrent: () => {
					skipChildren = true;
				},
				stopAll,
			});
			if (!skipChildren && !stopped) walk(component.children, childPath, component);
		}
	}
	walk(elements, initialPath);
}
